import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Award, Calendar, GraduationCap, Info, Loader, Play, SquareCheck, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useUserAttributes } from "../../queries/general";
import { formatDate } from "../../services";
import i18n from "../../services/i18n";
import type { JavaBasicDate, Level } from "../../types";
import { Icon } from "../Icon";

type IStatusBoxes = {
  id: number;
  credits?: number;
  isMandatory: boolean | undefined;
  endDate: JavaBasicDate | undefined;
  expirationDate: JavaBasicDate | undefined;
  isCompleted: boolean;
  inProgress?: boolean;
  isBooked?: boolean;
  isExpired: boolean;
  isExpirationDateHard?: boolean;
  startDate: JavaBasicDate | undefined;
  learningObjectTypology: Level;
  isToj?:boolean,
  isHighLightEdition?:boolean,
  isFavourite?:boolean;
  attributes?:number[],
  usedInsideBigBanner?:boolean
  isSuggestedAI?:boolean,
  isSuggestedHR?:boolean,
  isSuggestedManager?:boolean,
  isSurvey?:boolean,
  isTest?:boolean,
  hideMandatoryAndAttributes?:boolean,
  showMandatory?:boolean,
  isWaitingApproval?:boolean,
}

type MessageBoxProps = {
  backgroundColor: string;
  border: string;
  color: string;
  icon: JSX.Element;
  label: string;
  ariaLabel?: string;
};


export const StatusBoxes = ({
  credits,
  id,
  isMandatory,
  isBooked,
  isCompleted,
  inProgress=false,
  expirationDate,
  isExpired,
  isExpirationDateHard=false,
  startDate,
  endDate,
  isToj,
  isHighLightEdition=false,
  isFavourite=false,
  attributes=[],
  usedInsideBigBanner=false,
  isSuggestedAI=false,
  isSuggestedHR=false,
  isSuggestedManager=false,
  isSurvey=false,
  isTest=false,
  hideMandatoryAndAttributes=false,
  showMandatory=false,
  isWaitingApproval=false
}: IStatusBoxes) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const { attributesArray } = useUserAttributes();

  // scadenza "hard" o gia' superata: il badge diventa rosso, altrimenti resta arancione
  const isCritical = isExpirationDateHard || isExpired;

  const suggestedByAttributes = useMemo(() => {
    return [
      { label: t("HR_SUGGESTED"), value: isSuggestedHR },
      { label: t("AI_SUGGESTED"), value: isSuggestedAI },
      { label: t("MANAGER_SUGGESTED"), value: isSuggestedManager }
    ];
  }, [isSuggestedManager, isSuggestedHR, isSuggestedAI]);

  const statusBox = () => {
    const boxes: MessageBoxProps[] = [];


    if (isWaitingApproval) {
      boxes.push({
        backgroundColor: theme.customColors.backgroundWarning,
        border: `1px solid ${theme.customColors.borderWarning}`,
        color: theme.customColors.textTag,
        icon: <Loader size="0.875rem" color={ theme.customColors.textTag } />,
        label: t("object_available_once_approved")
      });
    }

    if (isTest) {
      boxes.push({
        ariaLabel: t("test"),
        backgroundColor: theme.customColors.backgroundSecondary,
        border: `1px solid ${theme.customColors.textTag}`,
        color: theme.customColors.textTag,
        icon: <Icon color={ theme.customColors.textTag } icon="test" size={ 14 } />,
        label: ""
      });
    }

    if (isSurvey) {
      boxes.push({
        ariaLabel: t("survey"),
        backgroundColor: theme.customColors.backgroundSecondary,
        border: `1px solid ${theme.customColors.textTag}`,
        color: theme.customColors.textTag,
        icon: <Icon color={ theme.customColors.textTag } icon="survey" size={ 14 } />,
        label: ""
      });
    }

    if (isCompleted) {
      boxes.push({
        backgroundColor: theme.customColors.backgroundSecondaryComplete,
        border: `1px solid ${theme.customColors.borderSecondaryComplete}`,
        color: theme.customColors.textTag,
        icon: <SquareCheck size="0.875rem" color={ theme.customColors.textTag } />,
        label: t("completed")
      });
    }

    const box = expiringDateBox();
    if (expirationDate && !isCompleted && isMandatory && !(expirationDate[0] === 9999) && box ) {
      boxes.push(box);
    }

    if ((!hideMandatoryAndAttributes || showMandatory) && isMandatory && !isCompleted && !isCritical) {
      boxes.push({
        backgroundColor: theme.customColors.backgroundWarning,
        border: `1px solid ${theme.customColors.borderWarning}`,
        color: theme.customColors.textTag,
        icon: <TriangleAlert size="0.875rem" color={ theme.customColors.textTag } />,
        label: t("mandatory")
      });
    }

    if ((!hideMandatoryAndAttributes || showMandatory) && isMandatory && !isCompleted && isCritical) {
      boxes.push({
        backgroundColor: theme.customColors.backgroundError,
        border: `1px solid ${theme.customColors.borderError}`,
        color: theme.customColors.textTag,
        icon: <TriangleAlert size="0.875rem" color={ theme.customColors.textTag } />,
        label: t("mandatory")
      });
    }

    if (isFavourite && location.pathname === "/bookmarks") {
      boxes.push({
        backgroundColor: theme.customColors.backgroundSecondary,
        border: `1px solid ${theme.customColors.textTag}`,
        color: theme.customColors.textTag,
        icon: <Info size="0.875rem" color={ theme.customColors.textTag } />,
        label: t("last_saved")
      });
    }
    return boxes.map((el)=>{
      const badgeSlug = String(el.label).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "status";
      return(
        <Box
          key={ el.label }
          tabIndex={ -1 }
          id={ `badge-container-${id}-${badgeSlug}` }
          data-testid="badge-container"
          aria-label={ el.ariaLabel || el.label || t("carousel_badge_aria_label") }
          sx={ {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "1.5625rem",
            padding: "0.10938rem 0.4375rem",
            gap: "0.25rem",
            border: el.border ?? "none",
            backgroundColor: el.backgroundColor,
            borderRadius: "0.25rem"
          } }
        >
          { el.icon }
          <Typography tabIndex={ -1 } id={ `${id}-card-statusBox` } sx={ { fontSize: "0.75rem", fontWeight: "600" } }
            color={ el.color }>
            { el.label }
          </Typography>
        </Box>
      );
    });
  };

  const expiringDateBox = (): MessageBoxProps|null => {

    const dateFormatted = () =>  {
      if(!startDate ){
        if (expirationDate){
          return {
            date: formatDate( expirationDate.join("/"), i18n.language),
            end:"",
            type:"normal"
          };
        }  else {
          return   {
            date:"",
            end:"",
            type:"normal"
          };
        }
      }
      else if (endDate && (startDate.join("/") !== endDate.join("/"))) {
        return {
          date: formatDate(startDate.join("/"), i18n.language),
          end:formatDate(endDate.join("/"), i18n.language),
          type: isToj ? "ecm_2" :"register_2"
        };
      }
      else {
        return {
          date: formatDate( startDate.join("/"), i18n.language),
          end:"",
          type:  isToj ? "ecm" :"register"
        };
      }
    };

    const labelRegister1 = ` ${!isHighLightEdition ? `${t("registered")} : ` : "  "} ${dateFormatted().date} `;
    const labelRegister2 = `${!isHighLightEdition ? `${t("registered")} : ` : " "}
  ${t("from")} ${dateFormatted().date} ${t("to")} ${dateFormatted().end}`;

    const labelEcm1 = `${dateFormatted().date} `;

    const labelEcm2= `${t("from")} ${dateFormatted().date} ${t("to")} ${dateFormatted().end}`;

    const finalLabel = dateFormatted().type === "register_2"
      ? labelRegister2
      : dateFormatted().type === "register"
        ? labelRegister1 :
        dateFormatted().type === "ecm_2"
          ? labelEcm2
          : dateFormatted().type === "ecm"
            ? labelEcm1
            : isExpired ? `${t("expired")}:${dateFormatted().date}` : `${dateFormatted().date}`;

    // la data "infinita" (expirationDate[0] === 9999) è già filtrata dal chiamante (statusBox());
    // qui basta verificare che ci sia una data da mostrare, senza dipendere dal formato localizzato
    if (!dateFormatted().date) return null;

    return {
      backgroundColor:isCritical ? theme.customColors.backgroundError : theme.customColors.backgroundWarning,
      border: isCritical
        ? `1px solid ${theme.customColors.borderError}`
        : `1px solid ${theme.customColors.borderWarning}`,
      icon: <Calendar size="0.875rem" color={ theme.customColors.textTag } />,
      color: theme.customColors.textTag,
      label: finalLabel
    };
  };

  const bookedBox =() => {
    return(
      <Box
        key={ "registered" }
        tabIndex={ -1 }
        id={ `badge-container-${id}-registered` }
        data-testid="badge-container"
        aria-label={ t("registered") }
        sx={ {
          alignItems: "center",
          backgroundColor: theme.customColors.backgroundSecondaryAlternative,
          border: `1px solid ${theme.customColors.backgroundAccent}`,
          borderRadius: "0.25rem",
          gap:"0.25rem",
          display: "flex",
          height: "1.5625rem",
          justifyContent: "center",
          padding: "0.10938rem 0.4375rem"
        } }
      >
        <Award size="0.875rem" color={ theme.customColors.textTag }/>
        <Typography
          tabIndex={ -1 }
          id={ "registered-badge-primary" }
          sx={ { fontSize: "0.75rem", fontWeight: "600" } }
          color={ theme.customColors.textTag }>
          { t("registered") }
        </Typography>
      </Box>
    );
  };

  const creditsBox =()=>{
    if(!credits) return <></>;

    return (
      <Box
        tabIndex={ -1 }
        id={ `badge-container-${id}-credits` }
        data-testid="badge-container"
        aria-label={ `${credits} ${t("campaigns_credits")}` }
        sx={ {
          alignItems: "center",
          backgroundColor: theme.customColors.backgroundSecondary,
          border: `1px solid ${theme.customColors.textTag}`,
          borderRadius: "0.25rem",
          gap:"0.25rem",
          display: "flex",
          height: "1.5625rem",
          justifyContent: "center",
          padding: "0.10938rem 0.4375rem"
        } }
      >
        <GraduationCap size="0.875rem" color={ theme.customColors.textTag }/>
        <Typography
          tabIndex={ -1 }
          id={ `${id}-credits-badge` }
          sx={ { fontSize: "0.75rem", fontWeight: "600" } }
          color={ theme.customColors.textTag }>
          { credits + " " + t("campaigns_credits") }
        </Typography>
      </Box>
    );
  };

  const primaryBadgeBox = () => {
    //prendo gli attributes dallo store per ricavare l'attributName dato che nel singolo LO ci arriva [idAttribute1,idAttribute2, ecc ecc]
    const attributesNames = [
      ...(hideMandatoryAndAttributes
        ? []
        : (attributesArray
          ?.filter(attr => attributes?.includes(Number(attr.attributeId)))
          ?.map(attr => attr.attributeName) ?? [])),
      ...suggestedByAttributes.filter((attr) => attr.value).map((attr) => attr.label)
    ];
    const remainAttributes = (attributesNames.length) -1;

    if(usedInsideBigBanner){
      return attributesNames.map((el,i) => {
        return (
          <Box
            key={ el+i }
            tabIndex={ -1 }
            id={ `badge-container-${id}-attribute-${i}` }
            data-testid="badge-container"
            aria-label={ el }
            sx={ {
              alignItems: "center",
              backgroundColor: theme.customColors.backgroundSecondaryAlternative,
              border: `1px solid ${theme.customColors.backgroundAccent}`,
              borderRadius: "0.25rem",
              gap:"0.25rem",
              display: "flex",
              height: "1.5625rem",
              justifyContent: "center",
              padding: "0.10938rem 0.4375rem"
            } }
          >
            <Award size="0.875rem" color={ theme.customColors.textTag }/>
            <Typography
              tabIndex={ -1 }
              id={ `${id}-primary-badge` }
              sx={ { fontSize: "0.75rem", fontWeight: "600" } }
              color={ theme.customColors.textTag }>
              { el }
            </Typography>
          </Box>
        );
      });
    }else{
      return (
        <>
          { attributesNames.length > 0 &&
            <Box
              key={ `attributesNames-${attributesNames[0]}` }
              tabIndex={ -1 }
              id={ `badge-container-${id}-attribute-primary` }
              data-testid="badge-container"
              aria-label={ attributesNames[0] }
              sx={ {
                alignItems: "center",
                backgroundColor: theme.customColors.backgroundSecondaryAlternative,
                border: `1px solid ${theme.customColors.backgroundAccent}`,
                borderRadius: "0.25rem",
                gap:"0.25rem",
                display: "flex",
                height: "1.5625rem",
                justifyContent: "center",
                padding: "0.10938rem 0.4375rem"
              } }
            >
              <Award size="0.875rem" color={ theme.customColors.textTag }/>
              <Typography
                tabIndex={ -1 }
                id={ `${id}-primary-badge-label` }
                sx={ { fontSize: "0.75rem", fontWeight: "600" } }
                color={ theme.customColors.textTag }>
                { attributesNames[0] }
              </Typography>
            </Box>
          }
          { attributesNames.length > 1 &&
            <Box
              key={ attributesNames.length }
              tabIndex={ -1 }
              id={ `badge-container-${id}-attribute-overflow` }
              data-testid="badge-container"
              aria-label={ `+${remainAttributes}` }
              sx={ {
                alignItems: "center",
                backgroundColor: theme.customColors.backgroundSecondaryAlternative,
                border: `1px solid ${theme.customColors.backgroundAccent}`,
                borderRadius: "0.25rem",
                gap:"0.25rem",
                display: "flex",
                height: "1.5625rem",
                justifyContent: "center",
                padding: "0.10938rem 0.4375rem"
              } }
            >
              <Typography
                tabIndex={ -1 }
                sx={ { fontSize: "0.75rem", fontWeight: "600" } }
                color={ theme.customColors.textTag }
              >
                { " " } + { remainAttributes }
              </Typography>
            </Box>
          }
        </>
      );
    }
  };

  const inProgressBox = () => {
    if(!inProgress) return <></>;

    return (
      <Box
        tabIndex={ -1 }
        id={ `badge-container-${id}-progress` }
        data-testid="badge-container"
        aria-label={ t("in_progress") }
        sx={ {
          alignItems: "center",
          backgroundColor: theme.customColors.backgroundSecondary,
          border: `1px solid ${theme.customColors.textTag}`,
          borderRadius: "0.25rem",
          gap:"0.25rem",
          display: "flex",
          height: "1.5625rem",
          justifyContent: "center",
          padding: "0.10938rem 0.4375rem"
        } }
      >
        <Play size="0.875rem" color={ theme.customColors.textTag }/>
        <Typography
          tabIndex={ -1 }
          id={ `${id}-badge-progress` }
          sx={ { fontSize: "0.75rem", fontWeight: "600" } }
          color={ theme.customColors.textTag }>
          { t("in_progress") }
        </Typography>
      </Box>
    );

  };

  return (
    <>
      { /*mandatory ,complete , expired date badges */ }
      { statusBox() }
      { /* crediti */ }
      { creditsBox() }

      { /* primary badge box */ }
      { primaryBadgeBox() }

      { /* in progress badge box */ }
      { inProgressBox() }

      { /* badge booking */ }
      { isBooked && bookedBox() }
    </>
  );
};
